// =============================================================================
// OrganizationSettingsPage — Organization profile & settings management
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Input, Button, Spinner, Badge, Modal, ModalFooter, Select } from '@tkpod/ui';
import {
  hasAnyPermission,
  OrgPermissions,
  updateOrganizationSchema,
} from '@tkpod/shared';
import type { OrgTaxProfileDto, TaxExemptionDocumentDto } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/lib/auth-store';
import { api } from '@/lib/api';

interface OrganizationData {
  id: string;
  name: string;
  slug: string;
  status: string;
  companyName: string | null;
  companyEmail: string | null;
  companyPhone: string | null;
  labelSenderName: string | null;
  taxId: string | null;
  vatNumber: string | null;
  billingAddress: string | null;
  shippingAddress: string | null;
  plan: {
    id: string;
    name: string;
    slug: string;
    storageLimit: number;
    exportsLimit: number;
    storesLimit: number;
    usersLimit: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface AvailablePlan {
  id: string;
  name: string;
  slug: string;
  monthlyPrice: string;
  yearlyPrice: string | null;
  discountPercentage: string;
  storageLimit: number;
  exportsLimit: number;
  storesLimit: number;
  usersLimit: number;
  features: string[];
}

type FieldErrors = Partial<Record<string, string>>;

function formatPlanPrice(price: string): string {
  const num = parseFloat(price);
  return num === 0 ? 'Free' : `$${num.toFixed(2)}/mo`;
}

function formatLimit(limit: number): string {
  return limit === -1 ? 'Unlimited' : String(limit);
}

export function OrganizationSettingsPage() {
  const { user } = useAuth();
  const reinitialize = useAuthStore((s) => s.reinitialize);
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [labelSenderName, setLabelSenderName] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const isOwner = user?.currentOrganization?.role === 'owner';
  const canEditTax = hasAnyPermission(
    user?.currentOrganization?.permissions ?? [],
    [
      OrgPermissions.ORGANIZATION_MANAGE,
      OrgPermissions.ORGANIZATION_UPDATE,
      OrgPermissions.SETTINGS_MANAGE,
    ],
  );

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const orgQuery = useQuery<OrganizationData>({
    queryKey: ['organization', 'current'],
    queryFn: () => api.get<OrganizationData>('/api/v1/organizations/current'),
    enabled: !!user?.currentOrganization,
  });

  const plansQuery = useQuery<{ data: AvailablePlan[] }>({
    queryKey: ['organization', 'plans'],
    queryFn: () => api.get<{ data: AvailablePlan[] }>('/api/v1/organizations/plans'),
    enabled: showPlanModal,
  });

  const changePlanMutation = useMutation<
    unknown,
    Error,
    { orgId: string; planId: string }
  >({
    mutationFn: ({ orgId, planId }) =>
      api.patch(`/api/v1/organizations/${orgId}/plan`, { planId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organization', 'current'] });
      await reinitialize();
      setShowPlanModal(false);
      setSelectedPlanId(null);
      setSuccessMessage('Plan updated successfully.');
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
    },
  });

  useEffect(() => {
    if (orgQuery.data && !initializedRef.current) {
      initializedRef.current = true;
      setName(orgQuery.data.name);
      setCompanyName(orgQuery.data.companyName ?? '');
      setCompanyEmail(orgQuery.data.companyEmail ?? '');
      setCompanyPhone(orgQuery.data.companyPhone ?? '');
      setLabelSenderName(orgQuery.data.labelSenderName ?? '');
    }
  }, [orgQuery.data]);

  const updateMutation = useMutation({
    mutationFn: ({
      orgId,
      ...data
    }: {
      orgId: string;
      name?: string;
      companyName?: string;
      companyEmail?: string;
      companyPhone?: string;
      labelSenderName?: string;
    }) => api.patch<OrganizationData>(`/api/v1/organizations/${orgId}`, data),
    onSuccess: async () => {
      setSuccessMessage('Organization settings saved successfully.');
      initializedRef.current = false;
      await queryClient.invalidateQueries({
        queryKey: ['organization', 'current'],
      });
      await reinitialize();
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(
        () => setSuccessMessage(null),
        3000,
      );
    },
  });

  const validate = useCallback((): boolean => {
    const payload = {
      name: name.trim() || undefined,
      companyName: companyName.trim() || undefined,
      companyEmail: companyEmail.trim() || undefined,
      companyPhone: companyPhone.trim() || undefined,
      // Empty string is intentional here — it clears the org default.
      labelSenderName: labelSenderName.trim(),
    };

    const result = updateOrganizationSchema.safeParse(payload);
    if (result.success) {
      setFieldErrors({});
      return true;
    }

    const errors: FieldErrors = {};
    for (const issue of result.error.issues) {
      const key = String(issue.path[0]);
      if (!errors[key]) errors[key] = issue.message;
    }
    setFieldErrors(errors);
    return false;
  }, [name, companyName, companyEmail, companyPhone, labelSenderName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);

    const orgId = orgQuery.data?.id;
    if (!orgId || !validate()) return;

    updateMutation.mutate({
      orgId,
      name: name.trim() || undefined,
      companyName: companyName.trim() || undefined,
      companyEmail: companyEmail.trim() || undefined,
      companyPhone: companyPhone.trim() || undefined,
      labelSenderName: labelSenderName.trim(),
    });
  };

  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  if (!user?.currentOrganization) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        No organization found. Please contact support.
      </div>
    );
  }

  if (orgQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (orgQuery.isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {orgQuery.error instanceof Error
            ? orgQuery.error.message
            : 'Failed to load organization data. Please try again later.'}
        </div>
      </div>
    );
  }

  if (!orgQuery.data) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        Failed to load organization data.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Profile */}
      <Card>
        <CardHeader
          title="Organization Profile"
          description="Update your organization details."
        />
        <form onSubmit={handleSubmit} className="mt-6 max-w-lg space-y-4 p-6 pt-0">
          {successMessage && (
            <div className="rounded-md bg-success/10 px-4 py-3 text-sm text-success">
              {successMessage}
            </div>
          )}

          {updateMutation.error && (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              {updateMutation.error instanceof Error
                ? updateMutation.error.message
                : 'Failed to save changes.'}
            </div>
          )}

          <Input
            label="Organization Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            placeholder="My Organization"
            error={fieldErrors.name}
          />

          <Input
            label="Slug"
            value={orgQuery.data?.slug ?? ''}
            disabled
            hint="Auto-generated and cannot be changed."
          />

          <Input
            label="Company Name"
            value={companyName}
            onChange={(e) => {
              setCompanyName(e.target.value);
              clearFieldError('companyName');
            }}
            placeholder="My Company LLC"
            error={fieldErrors.companyName}
          />

          <Input
            label="Company Email"
            type="email"
            value={companyEmail}
            onChange={(e) => {
              setCompanyEmail(e.target.value);
              clearFieldError('companyEmail');
            }}
            placeholder="contact@example.com"
            error={fieldErrors.companyEmail}
          />

          <Input
            label="Company Phone"
            value={companyPhone}
            onChange={(e) => {
              setCompanyPhone(e.target.value);
              clearFieldError('companyPhone');
            }}
            placeholder="+1-555-0100"
            error={fieldErrors.companyPhone}
          />

          <Input
            label="Shipping Label Sender Name"
            value={labelSenderName}
            onChange={(e) => {
              setLabelSenderName(e.target.value);
              clearFieldError('labelSenderName');
            }}
            placeholder="e.g. My Brand"
            maxLength={100}
            error={fieldErrors.labelSenderName}
            hint='Printed as the "from" name on shipping labels for all your stores. Each Shopify store can override this in its app settings. Leave empty to use the fulfillment provider name.'
          />

          <div className="pt-2">
            <Button type="submit" disabled={updateMutation.isPending || !orgQuery.data?.id}>
              {updateMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" /> Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </Card>

      {user?.currentOrganization?.id && (
        <BillingTaxCard
          orgId={user.currentOrganization.id}
          canEdit={canEditTax}
        />
      )}

      {/* Plan section */}
      {orgQuery.data?.plan && (
        <Card>
          <CardHeader
            title="Current Plan"
            description={isOwner
              ? 'Manage your organization plan.'
              : "Your organization's plan and limits."}
          />
          <div className="grid grid-cols-2 gap-4 p-6 pt-4 sm:grid-cols-4">
            <div>
              <p className="text-xs text-text-tertiary">Plan</p>
              <p className="text-sm font-semibold text-text-primary">
                <Badge variant="info">{orgQuery.data.plan.name}</Badge>
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Storage</p>
              <p className="text-sm font-semibold text-text-primary">
                {orgQuery.data.plan.storageLimit >= 1024
                  ? `${(orgQuery.data.plan.storageLimit / 1024).toFixed(0)} GB`
                  : `${orgQuery.data.plan.storageLimit} MB`}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Exports</p>
              <p className="text-sm font-semibold text-text-primary">
                {orgQuery.data.plan.exportsLimit.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Stores</p>
              <p className="text-sm font-semibold text-text-primary">
                {formatLimit(orgQuery.data.plan.storesLimit)}
              </p>
            </div>
          </div>

          {isOwner && (
            <div className="border-t border-border px-6 py-4">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedPlanId(orgQuery.data?.plan.id ?? null);
                  setShowPlanModal(true);
                }}
              >
                Change Plan
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Plan Selection Modal (Owner only) */}
      {showPlanModal && (
        <Modal
          open={showPlanModal}
          onClose={() => {
            setShowPlanModal(false);
            setSelectedPlanId(null);
            changePlanMutation.reset();
          }}
          title="Choose a Plan"
        >
          {plansQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" label="Loading plans..." />
            </div>
          ) : plansQuery.isError ? (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              Failed to load plans. Please try again.
            </div>
          ) : (
            <div className="space-y-3">
              {changePlanMutation.error && (
                <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                  {changePlanMutation.error.message || 'Failed to change plan.'}
                </div>
              )}

              {plansQuery.data?.data.map((plan) => {
                const isCurrent = plan.id === orgQuery.data?.plan.id;
                const isSelected = plan.id === selectedPlanId;

                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                      isSelected
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-border hover:border-brand-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-semibold text-text-primary">
                          {plan.name}
                        </span>
                        {isCurrent && (
                          <Badge variant="success" className="ml-2">Current</Badge>
                        )}
                      </div>
                      <span className="text-sm font-bold text-text-primary">
                        {formatPlanPrice(plan.monthlyPrice)}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-4 gap-2 text-xs text-text-secondary">
                      <span>{plan.storageLimit >= 1024
                        ? `${(plan.storageLimit / 1024).toFixed(0)} GB`
                        : `${plan.storageLimit} MB`} storage</span>
                      <span>{plan.exportsLimit.toLocaleString()} exports</span>
                      <span>{formatLimit(plan.storesLimit)} stores</span>
                      <span>{formatLimit(plan.usersLimit)} users</span>
                    </div>
                    {parseFloat(plan.discountPercentage) > 0 && (
                      <p className="mt-1 text-xs text-success">
                        {plan.discountPercentage}% product discount
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPlanModal(false);
                setSelectedPlanId(null);
                changePlanMutation.reset();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                !selectedPlanId ||
                selectedPlanId === orgQuery.data?.plan.id ||
                changePlanMutation.isPending
              }
              loading={changePlanMutation.isPending}
              onClick={() => {
                const orgId = orgQuery.data?.id;
                if (orgId && selectedPlanId) {
                  changePlanMutation.mutate({ orgId, planId: selectedPlanId });
                }
              }}
            >
              Confirm Plan Change
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

function BillingTaxCard({
  orgId,
  canEdit,
}: {
  orgId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [taxId, setTaxId] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');
  const [kind, setKind] = useState('RESALE');
  const [states, setStates] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const profileQuery = useQuery<{ data: OrgTaxProfileDto }>({
    queryKey: ['organization', orgId, 'tax-profile'],
    queryFn: () =>
      api.get<{ data: OrgTaxProfileDto }>(
        `/api/v1/organizations/${orgId}/tax-profile`,
      ),
  });

  const docsQuery = useQuery<{ items: TaxExemptionDocumentDto[] }>({
    queryKey: ['organization', orgId, 'tax-exemptions'],
    queryFn: () =>
      api.get<{ items: TaxExemptionDocumentDto[] }>(
        `/api/v1/organizations/${orgId}/tax-exemptions`,
      ),
  });

  useEffect(() => {
    if (profileQuery.data?.data && !hydrated) {
      const p = profileQuery.data.data;
      setTaxId(p.taxId ?? '');
      setVatNumber(p.vatNumber ?? '');
      setAddressLine1(p.addressLine1 ?? '');
      setAddressLine2(p.addressLine2 ?? '');
      setCity(p.city ?? '');
      setState(p.state ?? '');
      setPostalCode(p.postalCode ?? '');
      setCountry(p.country ?? 'US');
      setHydrated(true);
    }
  }, [profileQuery.data, hydrated]);

  const saveMutation = useMutation({
    mutationFn: (body: OrgTaxProfileDto) =>
      api.patch<{ data: OrgTaxProfileDto }>(
        `/api/v1/organizations/${orgId}/tax-profile`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'tax-profile'],
      });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a PDF, JPEG, or PNG file');
      const form = new FormData();
      form.append('file', file);
      form.append('kind', kind);
      if (states.trim()) form.append('states', states);
      if (expiresAt.trim()) form.append('expiresAt', expiresAt);
      return api.post<{ data: TaxExemptionDocumentDto }>(
        `/api/v1/organizations/${orgId}/tax-exemptions`,
        form,
      );
    },
    onSuccess: () => {
      setFile(null);
      queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'tax-exemptions'],
      });
    },
  });

  async function openCertificate(docId: string) {
    const res = await api.get<{ data: { url: string } }>(
      `/api/v1/organizations/${orgId}/tax-exemptions/${docId}/file`,
    );
    window.open(res.data.url, '_blank', 'noopener,noreferrer');
  }

  return (
    <Card>
      <CardHeader
        title="Billing & Tax Information"
        description="Tax IDs, billing address, and resale certificates. Certificates are reviewed by Transfer Kingdom — they are not self-approved."
      />
      <div className="max-w-lg space-y-4 p-6 pt-4">
        {profileQuery.isLoading ? (
          <Spinner />
        ) : (
          <>
            <Input
              label="Tax ID"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. 12-3456789"
            />
            <Input
              label="VAT Number"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              disabled={!canEdit}
            />
            <Input
              label="Address line 1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              disabled={!canEdit}
            />
            <Input
              label="Address line 2"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              disabled={!canEdit}
            />
            <Input
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={!canEdit}
            />
            <Input
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. CA"
            />
            <Input
              label="Postal code"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              disabled={!canEdit}
            />
            <Input
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              disabled={!canEdit}
              hint="ISO 2-letter code"
            />
            {canEdit && (
              <Button
                type="button"
                disabled={saveMutation.isPending}
                loading={saveMutation.isPending}
                onClick={() =>
                  saveMutation.mutate({
                    taxId,
                    vatNumber,
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    postalCode,
                    country,
                  })
                }
              >
                Save tax information
              </Button>
            )}
            {saveMutation.isError && (
              <p className="text-sm text-rose-600">
                {saveMutation.error instanceof Error
                  ? saveMutation.error.message
                  : 'Save failed'}
              </p>
            )}
            {saveMutation.isSuccess && (
              <p className="text-sm text-emerald-700">Tax information saved.</p>
            )}
          </>
        )}

        <div className="border-t border-border pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">
            Tax exemption certificates
          </h3>
          <p className="text-xs text-text-secondary">
            Upload a resale or exemption certificate (PDF, JPEG, or PNG, max
            10 MB). Review is performed by Transfer Kingdom; uploading does not
            exempt orders automatically.
          </p>
          {docsQuery.data?.items.length ? (
            <ul className="space-y-2">
              {docsQuery.data.items.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <button
                    type="button"
                    className="text-left text-brand-600 hover:underline"
                    onClick={() => void openCertificate(doc.id)}
                  >
                    {doc.originalFilename}
                  </button>
                  <Badge
                    variant={
                      doc.reviewStatus === 'APPROVED'
                        ? 'success'
                        : doc.reviewStatus === 'REJECTED'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {doc.reviewStatus}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-text-tertiary">No certificates uploaded.</p>
          )}
          {canEdit && (
            <div className="space-y-2">
              <Select
                label="Certificate type"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                options={[
                  { value: 'RESALE', label: 'Resale' },
                  { value: 'OTHER', label: 'Other' },
                ]}
              />
              <Input
                label="US states covered"
                value={states}
                onChange={(e) => setStates(e.target.value)}
                placeholder="CA, NY"
                hint="Comma-separated 2-letter codes. Optional."
              />
              <Input
                label="Expires"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block text-sm"
              />
              {uploadMutation.isError && (
                <p className="text-sm text-rose-600">
                  {uploadMutation.error instanceof Error
                    ? uploadMutation.error.message
                    : 'Upload failed'}
                </p>
              )}
              <Button
                type="button"
                variant="secondary"
                disabled={!file || uploadMutation.isPending}
                loading={uploadMutation.isPending}
                onClick={() => uploadMutation.mutate()}
              >
                Upload certificate
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
